import { Store } from 'redux'
import { AppState } from 'store/types'
import { mapOneSignalUserId, initializeOneSignal } from 'services/onesignal'
import { login as loginIntoGamelogic, getUserInfoAsStream, saveDevice, deleteDevice } from 'services/gamelogic'
import { startSession, updateSessionUserInfo, startAnonymousSession, setSessionError } from 'store/modules/session'
import delay from 'lib/delay'
import { initialize, getIdToken } from './auth/firebase'

export const getAuthToken = getIdToken

let eventSourceSubscription: () => void | undefined

const onAuthError = ({ dispatch }: Store<AppState>, err: Error) => {
  dispatch(setSessionError(err))
}

const onUserLogin = (store: Store<AppState>) => async () => {
  try {
    const loginResponse = await loginIntoGamelogic()
    const userInfoStream = await getUserInfoAsStream('me')

    eventSourceSubscription = await userInfoStream(
      result => {
        const { dispatch, getState } = store
        const usingLogin = getState().session.isUserInitialized

        if (getState().session.user) {
          dispatch(updateSessionUserInfo(result))
        } else {
          dispatch(startSession(loginResponse, result, usingLogin))
          mapOneSignalUserId(saveDevice)
        }
      },
      err => {
        onAuthError(store, err)
      },
    )
  } catch (err) {
    onAuthError(store, err)
  }
}

const beforeUserLogout = () => Promise.race([mapOneSignalUserId(deleteDevice), delay(3000)])

const onUserLogout = ({ dispatch, getState }: Store<AppState>) => async () => {
  const usingLogout = getState().session.isUserInitialized

  dispatch(startAnonymousSession(usingLogout))

  if (eventSourceSubscription) {
    eventSourceSubscription()
  }
}

const onUserSubscriptionChange = (store: Store<AppState>) => (isSubscribed: boolean) => {
  if (store.getState().session.user) {
    mapOneSignalUserId(isSubscribed ? saveDevice : deleteDevice)
  }
}

export const initializeSession = (store: Store<AppState>) => {
  initialize(
    onUserLogin(store),
    onUserLogout(store),
    beforeUserLogout,
  )
  initializeOneSignal(
    onUserSubscriptionChange(store),
  )
}
try {
    // @ts-ignore
    initializeSession.displayName = "initializeSession";
    // @ts-ignore
    initializeSession.__docgenInfo = { "description": "", "displayName": "initializeSession", "props": { "dispatch": { "defaultValue": null, "description": "Dispatches an action. It is the only way to trigger a state change.\n\nThe `reducer` function, used to create the store, will be called with the\ncurrent state tree and the given `action`. Its return value will be\nconsidered the **next** state of the tree, and the change listeners will\nbe notified.\n\nThe base implementation only supports plain object actions. If you want\nto dispatch a Promise, an Observable, a thunk, or something else, you\nneed to wrap your store creating function into the corresponding\nmiddleware. For example, see the documentation for the `redux-thunk`\npackage. Even the middleware will eventually dispatch plain object\nactions using this method.\n@param action A plain object representing \u201Cwhat changed\u201D. It is a good\nidea to keep actions serializable so you can record and replay user\nsessions, or use the time travelling `redux-devtools`. An action must\nhave a `type` property which may not be `undefined`. It is a good idea\nto use string constants for action types.\n@returns For convenience, the same action object you dispatched.\n\nNote that, if you use a custom middleware, it may wrap `dispatch()` to\nreturn something else (for example, a Promise you can await).", "name": "dispatch", "required": true, "type": { "name": "Dispatch<AnyAction>" } }, "getState": { "defaultValue": null, "description": "Reads the state tree managed by the store.\n@returns The current state tree of your application.", "name": "getState", "required": true, "type": { "name": "() => any" } }, "subscribe": { "defaultValue": null, "description": "Adds a change listener. It will be called any time an action is\ndispatched, and some part of the state tree may potentially have changed.\nYou may then call `getState()` to read the current state tree inside the\ncallback.\n\nYou may call `dispatch()` from a change listener, with the following\ncaveats:\n\n1. The subscriptions are snapshotted just before every `dispatch()` call.\nIf you subscribe or unsubscribe while the listeners are being invoked,\nthis will not have any effect on the `dispatch()` that is currently in\nprogress. However, the next `dispatch()` call, whether nested or not,\nwill use a more recent snapshot of the subscription list.\n\n2. The listener should not expect to see all states changes, as the state\nmight have been updated multiple times during a nested `dispatch()` before\nthe listener is called. It is, however, guaranteed that all subscribers\nregistered before the `dispatch()` started will be called with the latest\nstate by the time it exits.\n@param listener A callback to be invoked on every dispatch.\n@returns A function to remove this change listener.", "name": "subscribe", "required": true, "type": { "name": "(listener: () => void) => Unsubscribe" } }, "replaceReducer": { "defaultValue": null, "description": "Replaces the reducer currently used by the store to calculate the state.\n\nYou might need this if your app implements code splitting and you want to\nload some of the reducers dynamically. You might also need this if you\nimplement a hot reloading mechanism for Redux.\n@param nextReducer The reducer for the store to use instead.", "name": "replaceReducer", "required": true, "type": { "name": "(nextReducer: Reducer<any, AnyAction>) => void" } }, "__@observable": { "defaultValue": null, "description": "Interoperability point for observable/reactive libraries.\n@returns A minimal observable of state changes.\nFor more information, see the observable proposal:\nhttps://github.com/tc39/proposal-observable", "name": "__@observable", "required": true, "type": { "name": "() => Observable<any>" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/services/session.ts#initializeSession"] = { docgenInfo: initializeSession.__docgenInfo, name: "initializeSession", path: "src/services/session.ts#initializeSession" };
}
catch (__react_docgen_typescript_loader_error) { }